import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthenticationService,AppConfigService } from '@alfresco/adf-core';
import {
  MenuItemDto,
  MenuResponse,
  WorkflowCategory,
  WorkflowStage,
  ExpiryWarningLevel,
  ExpiryBadgeConfig,
  EXPIRY_BADGE_CONFIG
} from './menu.model';

/**
 * MenuService — single source of truth for dynamic menu configuration.
 *
 * Fetches the menu tree from GET /api/menu/items once on login,
 * caches it, and exposes three observables:
 *
 *   headerItems$      → top-level HEADER items for SpHeaderComponent
 *   workflowSidebar$  → SIDEBAR_CATEGORY + SIDEBAR_ITEM tree for WorkflowComponent
 *   allowedRoutes$    → set of permitted routes for MenuGuard
 *
 * Falls back gracefully to CORE-only items if the API is unavailable.
 */
@Injectable({ providedIn: 'root' })
export class MenuService {

  private apiUrl: string;

  //private readonly apiUrl = `${environment.apiUrl}/menu/items`;

  // ── Observables ──────────────────────────────────────────────────────────

  /** Top-level HEADER nav items */
  readonly headerItems$ = new BehaviorSubject<MenuItemDto[]>([]);

  /** Workflow sidebar categories (each contains .stages as children) */
  readonly workflowSidebar$ = new BehaviorSubject<WorkflowCategory[]>([]);

  /** System Admin sidebar items — children of the 'workflow-admin' header, license + role filtered server-side. */
  readonly adminSidebar$ = new BehaviorSubject<MenuItemDto[]>([]);

  /** Records Management sidebar items — children of the 'records-management' header (license/role filtered server-side). */
  readonly recordsSidebar$ = new BehaviorSubject<MenuItemDto[]>([]);

  /** Flat set of routes the current user is allowed to access */
  readonly allowedRoutes$ = new BehaviorSubject<Set<string>>(new Set());

  /** True once the first successful load has completed */
  readonly loaded$ = new BehaviorSubject<boolean>(false);

  /** Per-user seat access mode (FULL | READ_ONLY) — drives the view-only banner. */
  readonly accessMode$ = new BehaviorSubject<'FULL' | 'READ_ONLY'>('FULL');

  /** Fingerprint warning from the license service */
  readonly fingerprintWarning$ = new BehaviorSubject<boolean>(false);

  /** Current expiry warning level: NONE | INFO | WARNING | ALERT | CRITICAL | URGENT | EXPIRED */
  readonly expiryWarningLevel$ = new BehaviorSubject<ExpiryWarningLevel>('NONE');

  /** Days until license expiry (-1 = no expiry) */
  readonly daysUntilExpiry$ = new BehaviorSubject<number>(-1);

  /** License expiry date ISO string for display */
  readonly expiryDate$ = new BehaviorSubject<string | null>(null);

  /** Computed badge config for the current warning level */
  get expiryBadgeConfig(): ExpiryBadgeConfig {
    return EXPIRY_BADGE_CONFIG[this.expiryWarningLevel$.value];
  }

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private appConfig: AppConfigService
  ) {
    const apiBase = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.apiUrl = `${apiBase}/menu/items`;
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Load the menu from the backend.
   * Call this once after login — SpHeaderComponent calls it in ngOnInit.
   * Subsequent calls return the cached version unless forceReload=true.
   */
  loadMenu(forceReload = false): void {
    if (this.loaded$.value && !forceReload) return;

    const headers = this.getAuthHeaders();
    this.http.get<MenuResponse>(this.apiUrl, { headers })
      .pipe(
        tap(response  => this.processResponse(response)),
        catchError(err => {
          console.error('[MenuService] Failed to load menu from API:', err);
          this.applyFallbackMenu();
          return of(null);
        })
      )
      .subscribe();
  }

  /** Returns true if a given route is in the user's allowed routes set */
  isRouteAllowed(route: string): boolean {
    if (!this.loaded$.value) return true; // Don't block before first load
    const routes = this.allowedRoutes$.value;

    // Exact match
    if (routes.has(route)) return true;

    // Prefix match — e.g. /mail-workflow/task/123 is allowed if /mail-workflow is allowed
    if (Array.from(routes).some(r => route.startsWith(r + '/'))) return true;

    // Top-level segment match — e.g. extract /mail-workflow from /mail-workflow/task/123
    // and check if that top-level route is in the allowed set
    const topSegment = '/' + route.split('/').filter(Boolean)[0];
    if (routes.has(topSegment)) return true;

    return false;
  }

  /** Clear cache on logout */
  clearMenu(): void {
    this.headerItems$.next([]);
    this.workflowSidebar$.next([]);
    this.adminSidebar$.next([]);
    this.recordsSidebar$.next([]);
    this.accessMode$.next('FULL');
    this.allowedRoutes$.next(new Set());
    this.loaded$.next(false);
    this.expiryWarningLevel$.next('NONE');
    this.daysUntilExpiry$.next(-1);
    this.expiryDate$.next(null);
  }

  // ── Response processing ───────────────────────────────────────────────────

  private processResponse(response: MenuResponse): void {
    // Header items — top level (menuType === HEADER)
    const headerItems = response.items.filter(i => i.menuType === 'HEADER');
    this.headerItems$.next(headerItems);

    // Workflow sidebar — find the 'workflow' header item's children
    const workflowItem = response.items.find(i => i.menuKey === 'workflow');
    const categories   = this.buildWorkflowSidebar(workflowItem);
    this.workflowSidebar$.next(categories);

    // System Admin sidebar — children of the 'workflow-admin' header (already license/role filtered by the backend)
    const adminItem = response.items.find(i => i.menuKey === 'workflow-admin');
    this.adminSidebar$.next(adminItem?.children ?? []);

    // Records Management sidebar — children of the 'records-management' header
    const recordsItem = response.items.find(i => i.menuKey === 'records-management');
    this.recordsSidebar$.next(recordsItem?.children ?? []);

    // Allowed routes — collect all routes recursively
    const routes = new Set<string>();
    this.collectRoutes(response.items, routes);
    // Always allow core routes
    ['dashboard', 'profile', 'search', 'indexing', 'recent-files', 'favorites'].forEach(r => routes.add('/' + r));
    this.allowedRoutes$.next(routes);

    this.accessMode$.next(response.accessMode === 'READ_ONLY' ? 'READ_ONLY' : 'FULL');
    this.fingerprintWarning$.next(response.fingerprintWarning);

    // Expiry warning state
    const level = (response.expiryWarningLevel || 'NONE') as ExpiryWarningLevel;
    this.expiryWarningLevel$.next(level);
    this.daysUntilExpiry$.next(response.daysUntilExpiry ?? -1);
    this.expiryDate$.next(response.expiryDate || null);

    if (level !== 'NONE') {
      const days = response.daysUntilExpiry;
      const msg  = level === 'EXPIRED'
        ? '[MenuService] LICENSE EXPIRED for client: ' + response.licenseClient
        : `[MenuService] License expiry warning (${level}): ${days} days remaining`;
      console.warn(msg);
    }

    this.loaded$.next(true);
  }

  private buildWorkflowSidebar(workflowItem: MenuItemDto | undefined): WorkflowCategory[] {
    if (!workflowItem?.children) return [];

    return workflowItem.children
      .filter(c => c.menuType === 'SIDEBAR_CATEGORY')
      .map(cat => {
        const stages: WorkflowStage[] = (cat.children || [])
          .filter(s => s.menuType === 'SIDEBAR_ITEM')
          .map(s => ({
            id:           s.menuKey,
            name:         s.label,
            icon:         s.icon,
            workflowType: s.workflowType,
            description:  cat.label + ' — ' + s.label
          }));

        return {
          id:           cat.menuKey,
          name:         cat.label,
          icon:         cat.icon,
          color:        cat.color || '#244062',
          expanded:     false,
          workflowType: cat.workflowType,
          stages
        } as WorkflowCategory;
      });
  }

  /**
   * Maps each top-level menu route to all Angular sub-routes that belong to it.
   * When a top-level route is licensed, all its sub-routes are automatically allowed.
   * Add new sub-routes here whenever you add new Angular routes for a module.
   */
  private readonly SUB_ROUTE_MAP: Record<string, string[]> = {
    '/workflow': [
      '/workflow',
      '/mail-workflow',
      '/contract-workflow'
    ],
    '/contracts': [
      '/contracts',
      '/contract-wizard',
      '/contract-details',
      '/contracts/upload-executed'
    ],
    '/documents': [
      '/documents',
      '/recent-files',
      '/favorites',
      '/indexing'
    ]
    // '/case-management': [
    //   '/case-management',
    //   '/case-management/cases',
    //   '/case-management/new'
    // ]
  };

  private collectRoutes(items: MenuItemDto[], routes: Set<string>): void {
    for (const item of items) {
      if (item.route) {
        routes.add(item.route);
        // Also add all known sub-routes for this top-level route
        const subRoutes = this.SUB_ROUTE_MAP[item.route];
        if (subRoutes) subRoutes.forEach(r => routes.add(r));
      }
      if (item.children) this.collectRoutes(item.children, routes);
    }
  }

  /**
   * Fallback — show only CORE items when API is unreachable.
   * This ensures the app is usable even if the backend is temporarily down.
   */
  private applyFallbackMenu(): void {
    const fallback: MenuItemDto[] = [
      { menuKey: 'dashboard',       label: 'Dashboard',       icon: 'dashboard',     route: '/dashboard',       sortOrder: 10, color: null, menuType: 'HEADER', workflowType: null, children: null },
      { menuKey: 'my-work',         label: 'My Tasks',        icon: 'inbox',         route: '/my-work',         sortOrder: 15, color: null, menuType: 'HEADER', workflowType: null, children: null },
      { menuKey: 'documents',       label: 'Documents',       icon: 'folder_open',   route: '/documents',       sortOrder: 20, color: null, menuType: 'HEADER', workflowType: null, children: null },
      //{ menuKey: 'case-management', label: 'Case Management', icon: 'gavel',         route: '/case-management', sortOrder: 30, color: null, menuType: 'HEADER', workflowType: null, children: null }
    ];
    this.headerItems$.next(fallback);
    const routes = new Set<string>([
      '/dashboard', '/my-work', '/documents', '/recent-files', '/favorites',
      '/profile', '/search', '/indexing',
      '/workflow', '/mail-workflow', '/contract-workflow',
      '/contracts', '/contract-wizard', '/contract-details',
      '/contracts/upload-executed', '/reports', '/warehouse',
      // '/case-management', '/case-management/cases',
      // '/case-management/cases/:id', '/case-management/new'
    ]);
    this.allowedRoutes$.next(routes);
    this.workflowSidebar$.next([]);
    this.adminSidebar$.next([]);
    this.recordsSidebar$.next([]);
    this.loaded$.next(true);
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  private getAuthHeaders(): HttpHeaders {
    try {
      const token = this.authService.getToken();
      return token
        ? new HttpHeaders({ Authorization: `Bearer ${token}` })
        : new HttpHeaders();
    } catch {
      return new HttpHeaders();
    }
  }
}
